// src/views/bots/questions/TemplateQuestion.tsx
// 📄 TEMPLATE QUESTION COMPONENT - Fixed TypeScript version
// ✅ FIXED: All TypeScript errors resolved
// ✅ WORKS: Exactly like old TemplateSection but as a question type

import React, { useState, useEffect } from 'react';
import BaseQuestion from './BaseQuestion';
import UniversalTemplateSelector from '../../createWhatsappBroadcast/UnifiedTemplateSelector';
import WhatsAppPreview from '../../template/createTemplate/WhatsAppPreview';
import { QuestionItem } from '../hooks/useBotForm';
import questionStyles from './Questions.module.css';
import logger from '../../../utils/logger';

// 🎯 FIXED INTERFACES - Matches expected prop signatures
interface TemplateQuestionProps {
  question: QuestionItem;
  index: number;
  onUpdate: (id: string, field: keyof QuestionItem, value: any) => void;
  onRemove: (id: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  forceCollapsed?: boolean;
}

/**
 * 📄 TEMPLATE QUESTION COMPONENT
 * Handles template selection and preview - works exactly like old TemplateSection
 */
const TemplateQuestion: React.FC<TemplateQuestionProps> = ({
  question,
  index,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  logger.log('📄 TemplateQuestion: Rendered for question:', question.id, question.templateData);

  // 🎯 TEMPLATE STATES - Same as old system
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState<any>(null);

  // 🔥 EXTRACT TEMPLATE DATA - From question data
  const selectedTemplate = (question as any).templateData?.template;
  const templateId = (question as any).templateData?.templateId;
  const templateName = (question as any).templateData?.templateName;

  // 🎯 TEMPLATE SELECTOR HANDLERS - Same as old system
  const openTemplateSelector = () => {
    logger.log('📄 TemplateQuestion: Opening template selector');
    setShowTemplateSelector(true);
  };

  const closeTemplateSelector = () => {
    logger.log('📄 TemplateQuestion: Closing template selector');
    setShowTemplateSelector(false);
  };

  // 🔥 TEMPLATE SELECTION HANDLER - Fixed to use proper field update
  const handleTemplateSelect = (template: any) => {
    logger.log('📄 TemplateQuestion: Template selected:', template.name);
    
    // Update question with template data using the correct signature
    const templateData = {
      templateId: template.id || template._id,
      templateName: template.name,
      template: template
    };
    
    // Type-safe update
    onUpdate(question.id, 'templateData' as keyof QuestionItem, templateData);

    closeTemplateSelector();
  };

  // 🎯 PERSONALIZATION DETECTION - Same logic as old system
  const detectPersonalizationFields = (template: any) => {
    if (!template) return { hasFields: false, fields: [] };

    const bodyText = template.body || '';
    const headerText = template.header?.text || '';
    const footerText = template.footer?.text || '';
    
    const allText = `${bodyText} ${headerText} ${footerText}`;
    const fieldMatches = allText.match(/\{\{\d+\}\}/g) || [];
    const uniqueFields = [...new Set(fieldMatches)];
    
    return {
      hasFields: uniqueFields.length > 0,
      fields: uniqueFields
    };
  };

  // 🎯 LOAD TEMPLATE PREVIEW - Fixed with proper typing
  useEffect(() => {
    const loadTemplatePreview = async () => {
      if (!selectedTemplate) {
        setTemplatePreviewData(null);
        return;
      }

      logger.log('📄 TemplateQuestion: Loading preview for template:', selectedTemplate.name);
      logger.log('📄 TemplateQuestion: Full template object:', selectedTemplate);
      setTemplateLoading(true);

      try {
        // 🔥 FIXED: Properly extract template content with type annotations
        const templateBody = selectedTemplate.body || 
          selectedTemplate.components?.find((c: any) => c.type === 'BODY')?.text || '';
        const templateHeader = selectedTemplate.header?.text || 
          selectedTemplate.components?.find((c: any) => c.type === 'HEADER')?.text || '';
        const templateFooter = selectedTemplate.footer?.text || 
          selectedTemplate.components?.find((c: any) => c.type === 'FOOTER')?.text || '';
        const templateButtons = selectedTemplate.buttons || 
          selectedTemplate.components?.find((c: any) => c.type === 'BUTTONS')?.buttons || [];

        const previewData = {
          bodyValue: templateBody,
          values: {
            header: templateHeader,
            header_value: templateHeader,
            footer: templateFooter,
            haveButtons: templateButtons && templateButtons.length > 0 ? "yes" : "no",
            body: templateBody,
            buttons: templateButtons
          },
          file: null
        };

        logger.log('📄 TemplateQuestion: Preview data built with body:', templateBody);
        logger.log('📄 TemplateQuestion: Full preview data:', previewData);
        setTemplatePreviewData(previewData);
      } catch (error) {
        console.error('❌ TemplateQuestion: Error loading template preview:', error);
        setTemplatePreviewData(null);
      } finally {
        setTemplateLoading(false);
      }
    };

    loadTemplatePreview();
  }, [selectedTemplate]);

  // 🔥 PERSONALIZATION CHECK
  const personalizationInfo = selectedTemplate 
    ? detectPersonalizationFields(selectedTemplate)
    : { hasFields: false, fields: [] };

  return (
    <BaseQuestion
      question={question}
      index={index}
      onUpdate={onUpdate}
      onRemove={onRemove}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
    >
      {/* 🎯 TEMPLATE QUESTION CONTENT - Using children pattern */}
      <div className={questionStyles.templateQuestionContent}>
        
        {/* 🎯 TEMPLATE LOADING STATE */}
        {templateLoading ? (
          <div className={questionStyles.templateLoading}>
            <div className={questionStyles.loadingSpinner}></div>
            <span>Loading template preview...</span>
          </div>
        ) : selectedTemplate && templatePreviewData ? (
          /* 🎯 SELECTED TEMPLATE DISPLAY */
          <div className={questionStyles.selectedTemplate}>
            
            {/* Template Info Header */}
            <div className={questionStyles.templateHeader}>
              <div className={questionStyles.templateInfo}>
                <h4 className={questionStyles.templateName}>
                  📄 {selectedTemplate.name}
                </h4>
                <div className={questionStyles.templateMeta}>
                  <span className={questionStyles.templateStatus}>
                    {selectedTemplate.status || 'approved'}
                  </span>
                  <span className={questionStyles.templateLanguage}>
                    {selectedTemplate.language || 'en'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={openTemplateSelector}
                className={questionStyles.changeTemplateBtn}
              >
                Change Template
              </button>
            </div>

            {/* WhatsApp Preview - Fixed props */}
            <div className={questionStyles.templatePreview}>
              <div className={questionStyles.previewLabel}>
                WhatsApp Preview:
              </div>
              <WhatsAppPreview {...templatePreviewData} />
            </div>

            {/* 🔥 PERSONALIZATION WARNING - Same as old system */}
            {personalizationInfo.hasFields && (
              <div className={questionStyles.personalizationWarning}>
                <div className={questionStyles.warningHeader}>
                  <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                  <span>Personalization Fields Detected</span>
                </div>
                <p className={questionStyles.warningText}>
                  This template contains personalization fields: {personalizationInfo.fields.join(', ')}
                </p>
                <p className={questionStyles.warningSubtext}>
                  In questionnaire mode, these fields can be populated with collected user responses.
                </p>
              </div>
            )}

            {/* 🎯 TEMPLATE POSITION INFO */}
            <div className={questionStyles.templatePositionInfo}>
              <div className={questionStyles.positionHeader}>
                <i className="fas fa-info-circle" style={{ color: '#3b82f6' }}></i>
                <span>Template Position</span>
              </div>
              <p className={questionStyles.positionText}>
                This template will be sent at position {index + 1} in the conversation flow.
                You can reorder it using the move up/down buttons.
              </p>
            </div>
            
          </div>
        ) : (
          /* 🎯 NO TEMPLATE SELECTED - Same as old system */
          <div className={questionStyles.noTemplateSelected}>
            <button
              type="button"
              onClick={openTemplateSelector}
              className={questionStyles.selectTemplateBtn}
            >
              <div className={questionStyles.selectTemplateContent}>
                <i className="fa fa-plus-circle"></i>
                <span>Select Template</span>
                <small>
                  Choose a WhatsApp template to send at this point in the conversation
                </small>
              </div>
            </button>
          </div>
        )}

        {/* 🎯 TEMPLATE SELECTOR MODAL - Same as old system */}
        {showTemplateSelector && (
          <UniversalTemplateSelector
            isOpen={showTemplateSelector}
            onClose={closeTemplateSelector}
            onTemplateSelect={handleTemplateSelect}
            selectedTemplateId={selectedTemplate?._id}
            templateType="whatsapp"
            handleDiscard={closeTemplateSelector}
          />
        )}
        
      </div>
    </BaseQuestion>
  );
};

export default TemplateQuestion;